<template>
	<div
		:class="{'is-tagging-tool': isTaggingToolMode, 'is-disabled': disabled}"
		class="clip-list"
	>
		<TagListEmptyState
			v-if="isTaggingToolMode && clips.length === 0 && emptyStateEnabled && !isSearchActive"
			:has-highlight-detection="isHighlightsEnabled"
		/>
		<FilterableList
			v-else-if="hideEmptyList ? clips.length > 0 : true"
			ref="clipList"
			class="clip-list__inner-w"
			:title="$I18n.trans_choice('commons.clip', 2)"
			:filter-no-selection-label="noSelectionLabel"
			:active-filters="activeFiltersComp"
			:filters-module="filtersModuleComp"
			:hide-title-visibility="isSearchActive"
			header-padding-horizontal="md"
			header-padding-vertical="md"
			modifier="clip-list"
			@filters-updated="updateAndApplyActiveFilters"
		>
			<template #over-title>
				<div
					v-if="isSearchActive"
					class="search"
				>
					<ClipSearchInput
						:placeholder="transUcfirst('one.clip_search.search_in_clips')"
						:search-keyword="searchKeyword"
						@close="closeOrClearSearch"
						@change="onSearchInput"
					/>
				</div>
			</template>
			<template #filters-dropdown="{ filtersModule }">
				<div class="actions">
					<SearchButton
						v-if="isSearchBtnVisible && !isSearchActive"
						@click="isSearchActive=true"
					/>
					<SortButton
						v-if="!hideSort"
						:sort-dir="sortDir"
						:include-iq-score-sorting="hasTagsWithIQScore"
						@sort-updated="handleSortUpdated"
					/>
					<ClipFilters
						v-if="!hideFilter"
						:module-name="filtersModule"
						:show-filters-count="isMobileWebApp"
						:title="filtersListTitle"
						:search-keyword="searchKeyword"
						@list-updated="updateClips"
						@filters-updated="updateAndApplyActiveFilters"
					/>
				</div>
			</template>
			<template #list-view>
				<!-- sequential competition tabbed clip list -->
				<RaceSportClipList
					v-if="isTabbedMode"
					:event-clips="eventClips"
					:athlete-clips="athleteClips"
					:event="event"
					:sport-name="event.sportName"
					:empty-msg="emptyMsg"
					:active-tab="activeTab"
					:playing-clip-id="itemPlayingId"
					@play="handlePlay"
					@edit="handleEdit"
					@share="handleShare"
					@delete="handleDelete"
					@tab-change="handleTabChange"
				/>
				<!-- concurrent competition clip list -->
				<ListView
					v-else
					:items="clips"
					:empty-msg="noClipsAvailableMsg"
				>
					<template #no-results>
						<NoResults
							:title="emptyMsg.title"
							:description="emptyMsg.description"
						/>
					</template>
					<template #before-items>
						<EventBannerAd />
					</template>
					<template #item-row="{ item }">
						<ClipListTile
							:clip="item"
							:event-id="event.id"
							:is-playing="item.id === itemPlayingId"
							:hide-share="item.taggingToolOnly || hideShare"
							:hide-context-menu="hideContextMenu"
							:hide-likes="hideLikes"
							:hide-rating="hideRating"
							:can-edit="canEditEventTag(item)"
							@play="handlePlay"
							@edit="handleEdit"
							@share="handleShare"
							@delete="handleDelete"
							@update-rating="handleRatingUpdate"
						/>
					</template>
				</ListView>
			</template>
		</FilterableList>
		<ShareModal
			v-if="isShareModalOpen"
			:title="shareClipModalTitle"
			:event-title="eventTitle"
			:goal-score="goalScore"
			:item="shareTag"
			:item-label="transTagLabel(shareTag.name, event.sportName)"
			:url-label="urlLabel"
			:url="shareUrl"
			:links="shareLinks"
			@close="closeShareModal"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FilterableList from '../lists/FilterableList';
import { playerBus } from '@videotools/js/events';
import ShareModal from '../share-modal/ShareModal';
import ListView from '../lists/ListView';
import ClipFilters from './ClipFilters';
import SortButton from './SortButton';
import EventBannerAd from '@components/EventBannerAd';
import { TAG_NAMES, TAG_TYPE_CLOCK } from '@videotools/js/api/entities/TagButton';
import RaceSportClipList from '@components/clip-list/RaceSportClipList';
import ClipListTile from '@components/clip-list/ClipListTile';
import { TAB_ATHLETE_TAGS } from '@videotools/js/constants/athletes';
import ClipSearchInput from '@components/search/ClipSearchInput';
import SearchButton from '@components/search/SearchButton';
import NoResults from '@videotools/js/one/components/TagList/NoResults';
import TagListEmptyState from '@videotools/js/one/components/TagList/TagListEmptyState';

// t.b.c if this values is good enough
const SIDEBAR_LIVE_TAGS_FILTER_DELTA = 5;

export default {
	name:       'ClipList',
	components: {
		NoResults,
		SearchButton,
		ClipSearchInput,
		ClipListTile,
		RaceSportClipList,
		SortButton,
		ClipFilters,
		ListView,
		ShareModal,
		FilterableList,
		EventBannerAd,
		TagListEmptyState,
	},
	props: {
		hideLikes: {
			type:    Boolean,
			default: false,
		},
		hideShare: {
			type:    Boolean,
			default: false,
		},
		hideRating: {
			type:    Boolean,
			default: false,
		},
		hideContextMenu: {
			type:    Boolean,
			default: false,
		},
		hideFilter: {
			type:    Boolean,
			default: false,
		},
		hideSort: {
			type:    Boolean,
			default: false,
		},
		defaultSort: {
			type:    String,
			default: 'asc',
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		emptyStateEnabled: {
			type:    Boolean,
			default: true,
		},
		hideEmptyList: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isShareModalOpen:   false,
			shareTag:           {},
			goalScore:          '',
			shareLinks:         null,
			shareUrl:           null,
			onModalClose:       null,
			clipsData:          [],
			sortDir:            this.defaultSort,
			itemPlayingId:      null,
			isSearchActive:     false,
			isSearchBtnVisible: true,
		};
	},
	computed: {
		...mapGetters({
			activeTab:                 'app/sequentialCompetitionClipListActiveTab',
			isTaggingToolMode:         'app/isTaggingToolMode',
			isViewModeOrFullPageMode:  'app/isViewModeOrFullPageMode',
			isMobileWebApp:            'app/isMobileWebApp',
			isTouchDevice:             'device/isTouchDevice',
			supportsNativeShare:       'device/supportsNativeShare',
			eventTitle:                'event/eventTitle',
			event:                     'event/event',
			isLive:                    'event/isLive',
			sportName:                 'event/sportName',
			eventTagsForEvent:         'event/eventTagsForEvent',
			eventTagsForParticipant:   'event/eventTagsForParticipant',
			eventTags:                 'event/tags',
			isSequentialCompetition:   'event/isSequentialCompetition',
			hasTagsWithIQScore:        'event/hasTagsWithIQScore',
			streamLeadTimeSeconds:     'event/streamLeadTimeSeconds',
			activeAthleteFilters:      'athleteClipFilters/activeFilters',
			searchKeyWordStoreAthlete: 'athleteClipFilters/searchKeyword',
			activeFilters:             'clipFilters/activeFilters',
			sorting:                   'clipFilters/sorting',
			searchKeyWordStore:        'clipFilters/searchKeyword',
			isHighlightsEnabled:       'highlights/isHighlightsEnabled',
			isForParticipant:          'tags/isForParticipant',
			isForEvent:                'tags/isForEvent',
			canEditEventTag:           'permissions/canEditEventTag',
		}),
		isTabbedMode() {
			return this.isSequentialCompetition && !this.isViewModeOrFullPageMode;
		},
		clips() {
			return this.clipsData.filter(clip => {
				// filter out "future" tags in player timeline during live (when stream is behind incoming tags via API)
				// liveSyncPosition is more accurate than the video el duration (https://hlsjs.video-dev.org/api-docs/hls.js.hls.livesyncposition)
				const videoDuration = (playerBus.liveSyncPosition ?? playerBus.mediaDuration) - this.streamLeadTimeSeconds - SIDEBAR_LIVE_TAGS_FILTER_DELTA;
				const clipIsOutsideVideo = clip.isOutsideVideo;
				const clipIsFuture = this.isLive && (clip.timestamp > videoDuration);
				return !clipIsOutsideVideo && !clipIsFuture;
			});
		},
		filtersModuleComp() {
			if (!this.isTabbedMode) {
				return 'clipFilters';
			}
			return this.activeTab === TAB_ATHLETE_TAGS ? 'athleteClipFilters' : 'clipFilters';
		},
		activeFiltersComp() {
			if (!this.isTabbedMode) {
				return this.activeFilters;
			}
			return this.activeTab === TAB_ATHLETE_TAGS ? this.activeAthleteFilters : this.activeFilters;
		},
		shareClipModalTitle() {
			return this.shareLabels.SHARE_CLIP;
		},
		urlLabel() {
			return this.shareLabels.CLIP_URL;
		},
		noClipsAvailableMsg() {
			return this.$I18n.trans('commons.no_types_found', {
				type: this.$I18n.trans_choice('commons.clip', 2).toLowerCase(),
			});
		},
		noSelectionLabel() {
			if (!this.$I18n) {
				return 'All Clips';
			}
			return this.$I18n.trans('commons.all_types', {
				type: this.transChoiceUcFirst('commons.clip', 2),
			});
		},
		filtersListTitle() {
			return `${this.transUcfirst('commons.filter')} ${this.transChoiceUcFirst('commons.clip', 2)}`;
		},
		eventClips() {
			return this.clips.filter(evTag => this.isForEvent(evTag.tag)).filter(tag => !tag.player);
		},
		athleteClips() {
			return this.clips.filter(evTag => this.isForParticipant(evTag.tag) || evTag.playerId);
		},
		searchKeyword: {
			get() {
				if (this.isTabbedMode && this.activeTab === TAB_ATHLETE_TAGS) {
					return this.searchKeyWordStoreAthlete;
				}
				return this.searchKeyWordStore;
			},
			set() {
			},
		},
		emptyMsg() {
			if (this.searchKeyword?.length) {
				return {
					title:       this.transUcfirst('one.clip_search.no_results_for', { keyword: this.searchKeyword }),
					description: this.transUcfirst('one.clip_search.no_results_text'),
				};
			}

			return { title: this.$I18n.trans('commons.no_types_found', { type: this.$I18n.trans_choice('commons.clip', 2) }), description: '' };
		},
	},
	watch: {
		eventTags() {
			this.updateFilteredList();
		},
	},
	created() {
		if (this.sorting.ClipList) {
			this.sortDir = this.sorting.ClipList.direction;
		} else if (this.isLive) {
			this.sortDir = 'desc';
		} else {
			this.sortDir = this.defaultSort;
		}

		this.setExcludedTags(TAG_NAMES[TAG_TYPE_CLOCK]);
		if (this.isTabbedMode) {
			this.setSourceTagsEvent(this.eventTagsForEvent);
			this.setSourceTagsAthlete(this.eventTagsForParticipant);
		}
		this.isSearchActive = this.searchKeyword?.length > 0;
		this.updateFilteredList();

		window.addEventListener('resize', this.onWindowResize);
	},
	destroyed() {
		window.removeEventListener('resize', this.onWindowResize);
	},
	methods: {
		...mapActions({
			setActiveTab:               'app/setSequentialCompetitionClipListActiveTab',
			updateAthleteActiveFilters: 'athleteClipFilters/updateActiveFilters',
			setSourceTagsAthlete:       'athleteClipFilters/setSourceTags',
			searchAthleteClips:         'athleteClipFilters/searchClips',
			clearAthleteSearch:         'athleteClipFilters/clearSearch',
			updateActiveFilters:        'clipFilters/updateActiveFilters',
			setSourceTagsEvent:         'clipFilters/setSourceTags',
			setSorting:                 'clipFilters/setSorting',
			setExcludedTags:            'clipFilters/setExcludedTags',
			searchClips:                'clipFilters/searchClips',
			clearSearch:                'clipFilters/clearSearch',
			shareNative:                'device/shareNative',
			deleteEventTags:            'event/deleteEventTags',
			updateEventTag:             'event/updateEventTag',
			editEventTag:               'eventTagEdit/editEventTag',
			setTimelineActiveTags:      'timelineTags/setActiveTags',
		}),
		onWindowResize() {
			// make the search bar visible in case the mobile keyboard pushes it under the app tabs
			const el = document.getElementById('actions-wrapper')?.querySelector('.container-tabs .tabs');
			if (!el) {
				return;
			}
			const cliplistEl = this.$refs.clipList?.$el;

			if (!cliplistEl) {
				return;
			}
			const searchListHeader = cliplistEl.querySelector('.list--header');
			const searchEl = this.$refs.clipList.$el.querySelector('input.search-input');

			if (el && searchListHeader) {
				const tabsBox = el.getBoundingClientRect();
				const searchBox = searchListHeader.getBoundingClientRect();

				const isActive = document.activeElement === searchEl;

				if (searchBox.y < tabsBox.height + tabsBox.y && isActive) {
					document.scrollingElement.scrollTop = 180;
				}
			}
		},
		updateClips(clips) {
			this.clipsData = [...clips];
			this.updateTimelineTags();
			this.sortClips(this.sortDir);
		},
		updateFilteredList() {
			if (this.isTabbedMode) {
				this.updateClips([
					...this.$store.getters['clipFilters/filteredClips'],
					...this.$store.getters['athleteClipFilters/filteredClips'],
				]);
				return;
			}
			this.updateClips(this.$store.getters['clipFilters/filteredClips']);
		},
		async updateAndApplyActiveFilters(filters) {
			if (this.isTabbedMode && this.activeTab === TAB_ATHLETE_TAGS) {
				await this.updateAthleteActiveFilters(filters);
			} else {
				await this.updateActiveFilters(filters);
			}
			this.updateFilteredList();
		},
		updateTimelineTags() {
			if (this.isTaggingToolMode) {
				let clips;
				if (this.isTabbedMode) {
					clips = this.activeTab === TAB_ATHLETE_TAGS ? this.athleteClips : this.eventClips;
				} else {
					clips = this.clips;
				}
				this.setTimelineActiveTags(clips);
			}
		},
		handlePlay(clip) {
			if (clip) {
				this.itemPlayingId = clip.id;
				playerBus.seekTag(clip);
				playerBus.play();
				this.$emit('play', clip);
			}
		},
		handleEdit(clip) {
			this.editEventTag(clip);
		},
		async handleDelete(item) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('commons.clip', 2),
					}),
					body:   this.$I18n.trans('commons.delete_confirmation'),
					target: this.transTagLabel(item.name, this.sportName),
				});
				await this.deleteEventTags([item]);

				// The following fix was implemented: "Fullpage hotfix: Clip stays in list after deletion"
				// But it introduced this bug: https://www.notion.so/asport/Nach-dem-L-schen-eines-Tags-im-Tagging-Tool-werden-bis-zum-Reload-keine-Tags-mehr-angezeigt-66c774a8fa284c2b93924e4ba63c4391
				// So not sure if it actually solved the problem it intended to. Commenting it out for now which
				// makes it behave correclty in the Tagging Tool
				// const filterFn = (e) => e.id !== item.id;
				// this.setSourceTagsEvent(this.eventTagsForEvent.filter(filterFn));
				// this.setSourceTagsAthlete(this.eventTagsForParticipant.filter(filterFn));
				// this.updateFilteredList();
			} catch (_) {
			}
		},
		handleShare(clip) {
			this.openShareModal(clip);
		},
		handleSortUpdated(attribute, dir) {
			this.setSorting({
				component: 'ClipList',
				key:       attribute,
				direction: dir,
			});
			this.sortClips(attribute, dir);
		},
		async handleRatingUpdate({ rating, clip }) {
			try {
				await this.updateEventTag({
					eventId: this.event.id,
					tagId:   clip.id,
					body:    { rating },
				});
			} catch (e) {
				console.error(e);
			}
		},
		sortClips(attribute, dir = this.sortDir) {
			const ret = dir === 'asc' ? 1 : -1; // asc is oldest first
			this.sortDir = dir;
			this.clipsData.sort((tagA, tagB) => {
				return tagA[attribute] > tagB[attribute] ? ret : -ret;
			});
		},
		openShareModal(clip, onClose = () => {}) {
			this.shareTag = clip;
			this.shareUrl = clip.url;
			this.shareLinks = clip.shareLinks;
			if (clip.tag && clip.tag.name === 'goal') {
				const teamNameGoal = clip.team ? (clip.team.club ? clip.team.club.code : clip.team.name) : '';
				const teamHome = this.event.teamHome.club ? this.event.teamHome.club.code : this.event.teamHome.name;
				const teamGuest = this.event.teamGuest.club ? this.event.teamGuest.club.code : this.event.teamGuest.name;
				const scoreResult = this.clipsData
					.filter(el => {
						return el.tag.name === 'goal' && el.timestamp <= clip.timestamp;
					})
					.reduce(
						(acc, el) => {
							const teamCode = el.team ? (el.team.club ? el.team.club.code : el.team.name) : '';
							if (!acc[teamCode]) {
								acc[teamCode] = 1;
							} else {
								acc[teamCode]++;
							}
							return acc;
						},
						{
							[teamHome]:  0,
							[teamGuest]: 0,
						}
					);
				if (Object.keys(scoreResult).length !== 2) {
					console.log('Cannot calculate score...');
					this.goalScore = '';
				} else {
					this.goalScore = `(${Object.keys(scoreResult)
						.map(key => scoreResult[key])
						.join(':')}) ${teamNameGoal}`;
				}
			} else {
				this.goalScore = '';
			}
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: this.shareUrl,
				});
			} else {
				this.isShareModalOpen = true;
			}

			this.onModalClose = onClose;
		},
		closeShareModal() {
			this.isShareModalOpen = false;
			if (this.onModalClose) {
				this.onModalClose();
				this.onModalClose = null;
			}
		},
		handleTabChange(tab) {
			this.setActiveTab(tab);
			this.updateTimelineTags();
		},
		closeOrClearSearch() {
			if (this.searchKeyword === null) {
				this.isSearchActive = false;
			}
			if (this.isTabbedMode && this.activeTab === TAB_ATHLETE_TAGS) {
				this.clearAthleteSearch();
			} else {
				this.clearSearch();
			}
			this.updateFilteredList();
		},
		onSearchInput(value) {
			if (this.isTabbedMode && this.activeTab === TAB_ATHLETE_TAGS) {
				this.searchAthleteClips(value);
			} else {
				this.searchClips(value);
			}
			this.updateFilteredList();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts.scss';

.clip-list {
	&.is-disabled {
		opacity: 0.5;
		pointer-events: none;
		user-select: none;
		cursor: not-allowed;
	}
}

.clip-list__inner-w {
	.clip-list.is-tagging-tool & {
		background-color: var(--one-panel-background-color);
	}

}

.actions {
	display: flex;
	gap: $sp5;
}

.search {
	position: absolute;
	display: flex;
	align-items: center;
	width: 100%;
	height: 100%;
}
</style>
